class InstagramCarousel {
  static cache = {};

  constructor(section) {
    this.section = section;
    this.username = section.dataset.instagramUsername;
    this.sectionId = section.dataset.sectionId;
    
    if (!this.username) return;

    // Get settings from section
    this.columns = parseInt(section.dataset.productsPerView) || 3;
    this.mobileColumns = parseInt(section.dataset.mobileProductsPerView) || 1;
    this.loopCarousel = section.dataset.loopCarousel === 'true';

    // Get style settings from data attributes or defaults
    this.aspectRatio = 'square';
    this.labelStyle = 'overlay';
    this.labelSize = 'fs-body-100';
    this.overlayTextColor = '#fff';
    this.overlayTextBackground = '#000000';
    this.overlayBackgroundStyle = 'gradient';

    this.url = `https://reviews.weboptic.com/api/media?username=${this.username}&limit=25`;

    // Initialize
    this.allPosts = [];
    this.currentPosts = [];
    this.carousel = null;
    this.isInitialized = false;

    this.loadInstagramPosts();
  }

  async loadInstagramPosts() {
    try {
      const data = await this.fetchPosts();
      this.allPosts = data.media || [];
      this.currentPosts = this.allPosts;
      
      if (this.currentPosts.length > 0) {
        await this.renderPosts();
        // Add a small delay to ensure DOM is fully updated
        setTimeout(() => {
          this.initializeCarousel();
        }, 100);
      } else {
        this.showError('No Instagram posts found');
      }
    } catch (error) {
      console.error('Failed to load Instagram posts:', error);
      this.showError('Failed to load Instagram posts');
    }
  }

  async fetchPosts() {
    // Check memory cache first
    if (InstagramCarousel.cache[this.url]) {
      return InstagramCarousel.cache[this.url];
    }

    // Check localStorage cache
    const cached = localStorage.getItem(this.url);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < 10800000) { // 3 hours
          InstagramCarousel.cache[this.url] = parsed.data;
          return parsed.data;
        }
      } catch (e) {
        // Ignore cache errors
      }
    }

    // Fetch fresh data
    const response = await fetch(this.url);
    if (!response.ok) throw new Error('Failed to fetch Instagram posts');
    
    const data = await response.json();
    
    // Cache the data
    InstagramCarousel.cache[this.url] = data;
    localStorage.setItem(this.url, JSON.stringify({ 
      timestamp: Date.now(), 
      data 
    }));

    return data;
  }

  async renderPosts() {
    const wrapper = this.section.querySelector('.gallery-carousel__items');
    if (!wrapper) return;

    // Clear loading placeholder
    wrapper.innerHTML = '';

    // Create all slides first
    const slides = this.currentPosts.map((post, index) => this.createSlide(post, index));
    
    // Add all slides to wrapper
    slides.forEach(slide => wrapper.appendChild(slide));

    // Wait for images to load before initializing carousel
    return this.waitForImages(wrapper);
  }

  waitForImages(wrapper) {
    return new Promise((resolve) => {
      const images = wrapper.querySelectorAll('img');
      let loadedCount = 0;
      
      if (images.length === 0) {
        resolve();
        return;
      }

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= images.length) {
          resolve();
        }
      };

      images.forEach(img => {
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete);
          img.addEventListener('error', checkComplete);
        }
      });

      // Fallback timeout
      setTimeout(resolve, 3000);
    });
  }

  createSlide(post, index) {
    const slide = document.createElement('div');
    slide.className = 'gallery-carousel__item carousel__slide swiper-slide';

    // Handle video posts
    let imageUrl = post.media_url;
    if (post.media_type && post.media_type.toLowerCase() === "video") {
      imageUrl = post.thumbnail_url || "https://weboptic.com/assets/images/video-placeholder.jpg";
    }

    // Generate overlay CSS variables
    const overlayVars = this.generateOverlayVars();
    
    // Create caption and meta
    const captionText = this.getTruncatedText(post.caption);
    const timeAgo = this.getTimeAgo(post.timestamp);
    const stats = this.formatStats(post);

    slide.innerHTML = `
      <div class="
        gallery-item
        gallery-item--label-${this.labelStyle}
        gallery-item--label-background-${this.overlayBackgroundStyle}
        animation--item
      " style="${overlayVars} --delay-offset-multiplier: ${index + 1};">
        <div class="gallery-item__image-wrapper hover">
          <a href="${post.permalink}" target="_blank" rel="noopener noreferrer">
            <div class="image gallery-item__image aspect-ratio--${this.aspectRatio} animation--lazy-load" style="--focal-alignment: center;">
              <img 
                alt="${this.escapeHtml(captionText)}" 
                class="image__img" 
                loading="lazy" 
                src="${imageUrl}"
                onload="javascript: this.closest('.image').classList.add('loaded')"
              >
            </div>
          </a>
          ${this.labelStyle === 'overlay' ? `
            <div class="gallery-item__label ${this.labelSize} ff-body">
              ${captionText ? `<div class="instagram-post__caption">${captionText}</div>` : ''}
              ${timeAgo || stats ? `<div class="instagram-post__meta">${timeAgo}${stats}</div>` : ''}
            </div>
          ` : ''}
        </div>
        ${this.labelStyle === 'beneath' ? `
          <div class="gallery-item__label ${this.labelSize} ff-body">
            ${captionText ? `<div class="instagram-post__caption">${captionText}</div>` : ''}
            ${timeAgo || stats ? `<div class="instagram-post__meta">${timeAgo}${stats}</div>` : ''}
          </div>
        ` : ''}
      </div>
    `;

    return slide;
  }

  initializeCarousel() {
    // Prevent double initialization
    if (this.isInitialized) return;
    
    // Wait for theme's Carousel function to be available
    if (typeof Carousel === 'undefined') {
      setTimeout(() => this.initializeCarousel(), 100);
      return;
    }

    // Destroy existing carousel if it exists
    if (this.carousel) {
      this.carousel.destroy();
    }

    // Initialize using theme's Carousel function
    const perView = this.columns * 1.05; // Add peek effect
    const mobilePerView = this.mobileColumns * 1.05;

    // Add proper initialization parameters to prevent jumping
    this.carousel = Carousel(this.section, {
      slidesPerView: mobilePerView,
      spaceBetween: 12,
      loop: this.loopCarousel,
      initialSlide: 0, // Always start at first slide
      centeredSlides: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        720: {
          spaceBetween: 16,
          slidesPerView: perView
        },
        1200: {
          spaceBetween: 24,
          slidesPerView: perView
        }
      }
    });

    this.isInitialized = true;

    // Initialize animations if available
    if (typeof shouldAnimate !== 'undefined' && shouldAnimate(this.section)) {
      if (typeof animateListSlider !== 'undefined') {
        this.animateListSlider = animateListSlider(this.section);
      }
    }
  }

  generateOverlayVars() {
    let vars = `--overlay-text-color: ${this.overlayTextColor}; --label-background: ${this.overlayTextBackground};`;
    
    if (this.overlayBackgroundStyle === 'gradient') {
      const rgb = this.hexToRgb(this.overlayTextBackground);
      vars += ` --label-gradient-start: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4);`;
      vars += ` --label-gradient-mid: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2);`;
      vars += ` --label-gradient-end: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0);`;
    }
    
    return vars;
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  }

  formatStats(post) {
    let stats = [];
    if (post.likes_counts && post.likes_counts > 0) {
      stats.push(`❤️ ${post.likes_counts}`);
    }
    if (post.comments_count && post.comments_count > 0) {
      stats.push(`💬 ${post.comments_count}`);
    }
    return stats.length > 0 ? ` • ${stats.join(' • ')}` : '';
  }

  getTruncatedText(text) {
    if (!text) return '';
    if (text.length <= 100) return text;
    return text.slice(0, 100) + '...';
  }

  getTimeAgo(timestamp) {
    if (!timestamp) return '';
    const now = Date.now();
    const secondsAgo = Math.floor((now - new Date(timestamp).getTime()) / 1000);
    const intervals = [
      { label: 'year', seconds: 31536000 },
      { label: 'month', seconds: 2592000 },
      { label: 'day', seconds: 86400 },
      { label: 'hour', seconds: 3600 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 1 }
    ];
    for (const interval of intervals) {
      const count = Math.floor(secondsAgo / interval.seconds);
      if (count > 0) return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
    return 'just now';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showError(message) {
    const wrapper = this.section.querySelector('.gallery-carousel__items');
    if (!wrapper) return;

    wrapper.innerHTML = `
      <div class="gallery-carousel__item carousel__slide swiper-slide">
        <div class="instagram-error">
          <p style="text-align: center; padding: 2rem; color: #666;">
            ${message}
          </p>
        </div>
      </div>
    `;
  }

  // Cleanup method
  destroy() {
    this.isInitialized = false;
    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }
    if (this.animateListSlider) {
      this.animateListSlider.destroy();
      this.animateListSlider = null;
    }
  }
}

// Initialize Instagram carousels with better timing
document.addEventListener('DOMContentLoaded', () => {
  // Use shorter timeout and check for existing instances
  setTimeout(() => {
    document.querySelectorAll('[data-instagram-enabled="true"]').forEach(section => {
      if (!section.instagramCarouselInstance) {
        section.instagramCarouselInstance = new InstagramCarousel(section);
      }
    });
  }, 500);
});

// Register with theme's system if available
if (typeof register !== 'undefined') {
  register("gallery-carousel", {
    onLoad() {
      // Add small delay to prevent race conditions
      setTimeout(() => {
        if (this.container.dataset.instagramEnabled === 'true' && !this.container.instagramCarouselInstance) {
          this.container.instagramCarouselInstance = new InstagramCarousel(this.container);
        }
      }, 100);
    },
    onUnload() {
      if (this.container.instagramCarouselInstance) {
        this.container.instagramCarouselInstance.destroy();
        this.container.instagramCarouselInstance = null;
      }
    }
  });
}